import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
    BookOpen, Download, FileText, Image, FileSpreadsheet, 
    Paperclip, AlertCircle, Inbox, ArrowRight, ExternalLink 
} from 'lucide-react';
import '../styles/ExamCorner.scss';

const ExamCorner = () => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchExamFiles();
    }, []);

    const fetchExamFiles = async () => {
        try {
            setLoading(true);
            setError(null);

            const apiUrl = process.env.REACT_APP_API_URL || 'https://vhserver.desync.in';
            const response = await fetch(`${apiUrl}/public/api/school/ptvn_goraul/exam-corner`);
            const data = await response.json();

            if (data.success) {
                setFiles(data.data || []);
            } else {
                setError(data.message || 'Failed to load exam files');
            }
        } catch (err) {
            console.error('Error fetching exam files:', err);
            setError('Failed to load exam files. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const getFileIcon = (fileType) => {
        const type = (fileType || '').toLowerCase();
        if (type.includes('pdf')) {
            return <FileText size={32} color="#e53e3e" />;
        } else if (type.includes('image')) {
            return <Image size={32} color="#3182ce" />;
        } else if (type.includes('excel') || type.includes('spreadsheet') || type.includes('csv')) {
            return <FileSpreadsheet size={32} color="#38a169" />;
        } else {
            return <Paperclip size={32} color="#718096" />;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="exam-corner-container">
            <Helmet>
                <title>Exam Corner - Prabhat Tara Vidya Niketan</title>
                <meta name="description" content="Access exam schedules, time tables, question papers, results, and other academic resources at Prabhat Tara Vidya Niketan." />
                <meta name="keywords" content="exam, results, time table, question papers, academic resources, PTVN, Prabhat Tara Vidya Niketan" />
            </Helmet>

            <div className="exam-corner-content">
                <div className="page-header">
                    <span className="header-badge">
                        <BookOpen size={16} /> Examination Portal & Resources
                    </span>
                    <h1>Exam Corner & Academic Downloads</h1>
                    <p>Official exam timetables, assessment circulars, sample papers, and academic scorecards</p>
                </div>

                {/* ScanScore Smart Assessment Banner */}
                <div className="exam-scanscore-banner">
                    <div className="banner-copy">
                        <div className="partner-tag">
                            ⚡ SMART EVALUATION PARTNER
                        </div>
                        <h3>
                            Objective Exams Evaluated with ScanScore (scanscore.app)
                        </h3>
                        <p>
                            PTVN utilizes phone-based OMR scanning on plain A4 sheets, delivering instant test results and deep diagnostic feedback for our students without manual grading delays.
                        </p>
                    </div>
                    <div className="banner-actions">
                        <a 
                            href="/scanscore-assessment"
                            className="btn-read-more"
                        >
                            <span>Read How It Works</span>
                            <ArrowRight size={15} />
                        </a>
                        <a 
                            href="https://scanscore.app"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-visit-site"
                        >
                            <span>Visit scanscore.app</span>
                            <ExternalLink size={14} />
                        </a>
                    </div>
                </div>

                {loading && (
                    <div className="loading-container">
                        <div className="loader"></div>
                        <p>Loading exam files...</p>
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        <AlertCircle size={48} color="#e53e3e" style={{ margin: '0 auto 1rem' }} />
                        <p>{error}</p>
                        <button onClick={fetchExamFiles} className="retry-btn">
                            Try Again
                        </button>
                    </div>
                )}

                {!loading && !error && files.length === 0 && (
                    <div className="no-files-message">
                        <Inbox size={56} color="#a0aec0" style={{ margin: '0 auto 1rem' }} />
                        <h3>No Files Available</h3>
                        <p>There are currently no exam-related files available. Please check back later.</p>
                    </div>
                )}

                {!loading && !error && files.length > 0 && (
                    <div className="files-grid">
                        {files.map((file) => (
                            <div key={file._id} className="file-card">
                                <div className="file-icon">
                                    {getFileIcon(file.fileType)}
                                </div>
                                <div className="file-details">
                                    <h3 className="file-title">{file.title}</h3>
                                    <div className="file-meta">
                                        <span className="file-size">
                                            {formatFileSize(file.fileSize)}
                                        </span>
                                        <span className="file-date">
                                            {formatDate(file.createdAt)}
                                        </span>
                                    </div>
                                </div>
                                <a
                                    href={file.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="download-btn"
                                    aria-label={`Download ${file.title}`}
                                >
                                    <Download size={16} /> Download
                                </a>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExamCorner;
